import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { ReactNode } from 'react';

import type { CourseModel, ParticipantExtendedModel } from '../../../Courses/data/types';

import {
  areSwitchableDesktopListsEqual,
  getNextDesktop,
  getPreviousDesktop,
  getSwitchableDesktops,
  type SwitchableDesktop
} from './desktop-switcher-utils';
import { exitBrowserFullscreen } from './fullscreen-utils';

import type { ParticipantModel } from 'Api';

interface DesktopFullscreenState {
  isFullscreen: boolean;
  isBrowserFullscreen: boolean;
  currentDesktopId: string | null;
  switchableDesktops: SwitchableDesktop[];
  currentDesktop: SwitchableDesktop | null;
  currentDesktopIndex: number;
  totalDesktops: number;
  canSwitch: boolean;
  hasMachineControl: boolean;
  isTrainer: boolean;
  viewerRef: React.RefObject<HTMLDivElement | null>;
}

interface DesktopFullscreenActions {
  enterFullscreen: (desktopId: string) => void;
  exitFullscreen: () => void;
  switchToNext: () => void;
  switchToPrevious: () => void;
  initializeDesktops: (
    course: CourseModel,
    currentUserId: string,
    filteredParticipants: ParticipantExtendedModel[],
    trainer: ParticipantModel
  ) => void;
}

type DesktopFullscreenContextType = DesktopFullscreenState & DesktopFullscreenActions;

const DesktopFullscreenContext = createContext<DesktopFullscreenContextType | undefined>(undefined);

interface DesktopFullscreenProviderProps {
  children: ReactNode;
}

export const DesktopFullscreenProvider: React.FC<DesktopFullscreenProviderProps> = ({
  children
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);
  const [currentDesktopId, setCurrentDesktopId] = useState<string | null>(null);
  const [switchableDesktops, setSwitchableDesktops] = useState<SwitchableDesktop[]>([]);
  const [hasMachineControl, setHasMachineControl] = useState(false);
  const [isTrainer, setIsTrainer] = useState(false);

  const viewerRef = useRef<HTMLDivElement>(null);

  const currentDesktop = switchableDesktops.find(d => d.id === currentDesktopId) ?? null;
  const currentDesktopIndex = currentDesktop ? switchableDesktops.indexOf(currentDesktop) : 0;
  const totalDesktops = switchableDesktops.length;
  const canSwitch = totalDesktops > 1;

  const enterFullscreen = (desktopId: string) => {
    setCurrentDesktopId(desktopId);

    document.documentElement
      .requestFullscreen()
      .then(() => {
        setIsBrowserFullscreen(true);
        setIsFullscreen(true);
      })
      .catch(err => {
        console.warn('Failed to enter browser fullscreen:', err);
      });
  };

  const exitFullscreen = () => {
    exitBrowserFullscreen().finally(() => {
      setTimeout(() => {
        setIsFullscreen(false);
        setIsBrowserFullscreen(false);
        setCurrentDesktopId(null);
      }, 100);
    });
  };

  const switchToNext = () => {
    if (!canSwitch || !currentDesktop) {
      return;
    }

    const nextDesktop = getNextDesktop(switchableDesktops, currentDesktopIndex);
    if (nextDesktop) {
      setCurrentDesktopId(null);
      setTimeout(() => {
        setCurrentDesktopId(nextDesktop.id);

        const restoreFocus = () => {
          if (viewerRef.current && document.fullscreenElement) {
            viewerRef.current.focus();
          }
        };

        setTimeout(restoreFocus, 100);
        setTimeout(restoreFocus, 500);
        setTimeout(restoreFocus, 1000);
      }, 50);
    }
  };

  const switchToPrevious = () => {
    if (!canSwitch || !currentDesktop) {
      return;
    }

    const prevDesktop = getPreviousDesktop(switchableDesktops, currentDesktopIndex);
    if (prevDesktop) {
      setCurrentDesktopId(null);
      setTimeout(() => {
        setCurrentDesktopId(prevDesktop.id);

        const restoreFocus = () => {
          if (viewerRef.current && document.fullscreenElement) {
            viewerRef.current.focus();
          }
        };

        setTimeout(restoreFocus, 100);
        setTimeout(restoreFocus, 500);
        setTimeout(restoreFocus, 1000);
      }, 50);
    }
  };

  const initializeDesktops = (
    course: CourseModel,
    currentUserId: string,
    filteredParticipants: ParticipantExtendedModel[],
    trainer: ParticipantModel
  ) => {
    const desktops = getSwitchableDesktops(course, currentUserId, filteredParticipants, trainer);

    setSwitchableDesktops(prev => {
      if (areSwitchableDesktopListsEqual(prev, desktops)) {
        return prev;
      }

      return desktops;
    });

    setHasMachineControl(course.participantsMachineControlActive ?? false);
    setIsTrainer(course.isTrainer ?? false);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && currentDesktopId) {
        exitFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [currentDesktopId]);

  const contextValue: DesktopFullscreenContextType = {
    isFullscreen,
    isBrowserFullscreen,
    currentDesktopId,
    switchableDesktops,
    currentDesktop,
    currentDesktopIndex,
    totalDesktops,
    canSwitch,
    hasMachineControl,
    isTrainer,
    viewerRef,
    enterFullscreen,
    exitFullscreen,
    switchToNext,
    switchToPrevious,
    initializeDesktops
  };

  return (
    <DesktopFullscreenContext.Provider value={contextValue}>
      {children}
    </DesktopFullscreenContext.Provider>
  );
};

export const useDesktopFullscreen = (): DesktopFullscreenContextType => {
  const context = useContext(DesktopFullscreenContext);

  if (context === undefined) {
    throw new Error('useDesktopFullscreen must be used within a DesktopFullscreenProvider');
  }

  return context;
};

export const useDesktopFullscreenSafe = (): DesktopFullscreenContextType | null => {
  const context = useContext(DesktopFullscreenContext);
  return context ?? null;
};
